import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SCOPE_ID } from '@/lib/api-client';
import { authContainer } from '../../di/container';
import { useAuthStore } from '../store/auth.store';

const NO_PERK_KEYS: string[] = [];

/**
 * As perk keys que dão ao usuário logado autoridade de conceder num perfil do
 * scope `scopeId` — a mesma régua do `assertCanGrant` do back: as do scope
 * deste app (no snapshot, onde moram os curingas) somadas às do perfil dele no
 * scope do alvo, que só se buscam quando é outro scope.
 *
 * Falha (perfil inativo naquele scope, rede) não vira autoridade nenhuma — o
 * back faz o mesmo e segue sendo quem decide —, mas sai em `isError`: lista
 * vazia por falha não pode se passar por falta de permissão na tela.
 */
export function useGranterPerkKeys(scopeId: string): {
  keys: string[];
  isLoading: boolean;
  isError: boolean;
} {
  const appKeys = useAuthStore((s) => s.auth?.roles) ?? NO_PERK_KEYS;
  const otherScope = !!scopeId && scopeId !== SCOPE_ID;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['auth', 'me', 'perks', scopeId],
    queryFn: () => authContainer.repository.getMyPerkKeysInScope(scopeId),
    enabled: otherScope,
    retry: false,
  });
  const targetKeys = (otherScope && data) || NO_PERK_KEYS;
  const keys = useMemo(
    () => [...appKeys, ...targetKeys],
    [appKeys, targetKeys],
  );
  return {
    keys,
    isLoading: otherScope && isLoading,
    isError: otherScope && isError,
  };
}
